'use client';

import { getDictionary } from '@/utils/dictionaries';
import { ICheckoutRequestBody } from '@/api/[bu]/orders/route';
import { URL } from '@/constants/application-urls';
import useFetcher from '@/utils/hooks/useFetcher';
import { useMutation, useQueries, useQueryClient } from '@tanstack/react-query';
import { useContext, useEffect, useState } from 'react';
import { OrderContext } from '../context/OrderContext';
import { useCustomRouter } from '@/utils/hooks/useCustomRouter';
import { ToastStatus } from '@/enums/ToastStatus';
import { AreaContext } from '@/utils/contexts/AreaContext';
import AreaContextType from '@/enums/AreaContextType';
import { BUContext } from '@/utils/contexts/BusinessUnitContext';
import { useToast } from '@/features/FeatureToast/client/hooks/useToast';
import { useSession } from 'next-auth/react';
import { Price } from '@/types/Price';
import { OperationResultContext } from '@/utils/contexts/OperationResultContext';
import { IconName, IconType, IconSize } from '@/enums/IconSettings';
import QueryKeys from '@/enums/QueryKeys';
import { ICart, UpdateCartAction, UpdateCartActionType } from '@/types/Cart';
import CartType from '@/enums/CartType';

export type HandleUpdateCartFunction = (
	sku: string,
	itemId: string,
	action: UpdateCartActionType,
	quantity?: number,
) => Promise<any>;

export default function useCart({
	pathName,
	lang,
	purchaseOrderNumber,
	requestedDeliveryDate,
}: {
	pathName?: string;
	lang?: string;
	purchaseOrderNumber?: string;
	requestedDeliveryDate?: string;
} = {}) {
	const areaContext = useContext(AreaContext);

	const router = useCustomRouter();

	const queryClient = useQueryClient();
	const {
		data: { shippingAddress, billingAddress },
	} = useContext(OrderContext);

	const bu = useContext(BUContext);
	const [isBelowMinimumOrderThreshold, setIsBelowMinimumOrderThreshold] = useState<boolean>(false);
	const [minimumOrderValueThreshold, setMinimumOrderValueThreshold] = useState<number>(0);
	const [showRequiredApprovalMessage, setShowRequiredApprovalMessage] = useState<boolean | undefined>(false);
	const { showToast } = useToast();

	const { fetcher } = useFetcher();

	const [{ data: cart, isFetching, isError }, { data: cartDict }, { data: thankYouDict }, { data: hasApprovalRules }] =
		useQueries({
			queries: [
				{
					queryKey: [QueryKeys.CART, bu],
					queryFn: (): Promise<ICart> => fetcher(`/api/${bu}/cart/${CartType.Product}`),
					staleTime: 0,
					enabled: areaContext === AreaContextType.DEFAULT,
				},
				{
					queryKey: [QueryKeys.DICTIONARY, lang, 'cart'],
					queryFn: () => {
						return getDictionary(lang, 'cart');
					},
					staleTime: 0,
					enabled: !!lang,
				},
				{
					queryKey: [QueryKeys.DICTIONARY, lang, 'thank-you'],
					queryFn: () => {
						return getDictionary(lang, 'thank-you');
					},
					staleTime: 0,
					enabled: !!lang,
				},
				{
					queryKey: [QueryKeys.CHECK_APPROVAL, bu],
					queryFn: (): Promise<boolean> => fetcher(`/api/${bu}/cart/check-approval`),
					enabled: areaContext === AreaContextType.DEFAULT,
				},
			],
		});

	const { data: session } = useSession();

	useEffect(() => {
		function hasCompletedShippingDetails() {
			const details = shippingAddress;
			return !!details.streetName && !!details.city && !!details.postalCode && !!details.streetNumber;
		}

		function hasEmptyCart() {
			return !pathName?.endsWith('/cart') && cart?.lineItems && cart.lineItems.length == 0;
		}

		// Check if cart is empty and navigate to cart page
		const splitPath = pathName?.split('/');
		if (hasEmptyCart() && splitPath) {
			router.push(URL.CART);
			return;
		}

		// Check if shipping details are missing and navigate to shipping page
		if (!hasCompletedShippingDetails() && pathName?.endsWith('/cart/checkout')) {
			router.push(URL.SHIPPING);
		}

		// Check if total price is below minimum order value threshold
		const minimumOrderValue = session?.company?.minimumOrderValues?.find(
			(mov: Price) => mov.currencyCode === cart?.totalPrice?.currencyCode,
		);

		setMinimumOrderValueThreshold(minimumOrderValue?.value ?? 0);
		setIsBelowMinimumOrderThreshold(minimumOrderValueThreshold >= (cart?.totalPrice?.value ?? 0));
		setShowRequiredApprovalMessage(hasApprovalRules);
	}, [session?.company, cart, pathName, router, shippingAddress, minimumOrderValueThreshold, hasApprovalRules]);

	const { mutate: updateCartMutation, isLoading } = useMutation({
		mutationFn: (variables: UpdateCartAction) => {
			let localQuantity = variables.quantity ? Number(variables.quantity) : 1;
			localQuantity = variables.action === 'delete' ? 0 : localQuantity;

			return fetcher(`/api/${bu}/cart/${CartType.Product}/update`, {
				method: 'POST',
				body: JSON.stringify({
					lineItems: [{ lineItemId: variables.lineItemId, quantity: localQuantity, sku: variables.sku }],
					updateType: variables.action === 'add' ? 1 : 2,
					ignoreInvalidSkus: true,
				}),
			});
		},
		onSuccess: (data) => {
			queryClient.setQueryData([QueryKeys.CART, bu], data.cart);
		},
	});

	const handleUpdateCart: HandleUpdateCartFunction = async (
		sku: string,
		itemId: string,
		action: UpdateCartActionType,
		quantity?: number,
	): Promise<any> => {
		return new Promise((resolve) => {
			!isLoading &&
				updateCartMutation(
					{
						lineItemId: itemId,
						sku,
						action,
						quantity,
					},
					{
						onSuccess: () => {
							return resolve(true);
						},
						onError: () => {
							return resolve(false);
						},
					},
				);
		});
	};

	const operationResultContext = useContext(OperationResultContext);

	const checkoutOrders = useMutation({
		mutationFn: (variables: ICheckoutRequestBody) => {
			return fetcher(`/api/${bu}/orders`, {
				method: 'POST',
				body: JSON.stringify(variables),
			});
		},
		onSuccess: (response) => {
			if (!response.approvalState) {
				operationResultContext?.setOperationResultContextData({
					title: thankYouDict.success.title,
					description: thankYouDict.success.description,
					iconSettings: {
						name: IconName.CHECK_CIRCLE_ICON,
						type: IconType.SUCCESS,
						size: IconSize.LARGE,
					},
				});
			} else {
				operationResultContext?.setOperationResultContextData({
					title: thankYouDict.forApproval.title,
					description: thankYouDict.forApproval.description,
					iconSettings: {
						name: IconName.CLOCK_ICON,
						type: IconType.WARNING,
						size: IconSize.LARGE,
					},
				});
			}
		},
		onError: () => {
			showToast(cartDict.toast.error, ToastStatus.ERROR);

			operationResultContext?.setOperationResultContextData({
				title: thankYouDict.error.title,
				description: thankYouDict.error.description,
				iconSettings: {
					name: IconName.EXCLAMATION_TRIANGLE_ICON,
					type: IconType.ERROR,
					size: IconSize.LARGE,
				},
			});

			//show error exception
			console.error('error');
		},

		onSettled() {
			router.push(URL.THANKYOU);
			queryClient.setQueryData([QueryKeys.CART, bu], null);
		},
	});

	const handleCheckoutClick = () => {
		checkoutOrders.mutate({
			shippingAddress: {
				...shippingAddress,
			},
			billingAddress: {
				...billingAddress,
			},
			...(purchaseOrderNumber && { purchaseOrderNumber }),
			...(requestedDeliveryDate && { requestedDeliveryDate }),
		});
	};

	return {
		cart,
		handleUpdateCart,
		handleCheckoutClick,
		isBelowMinimumOrderThreshold,
		minimumOrderValueThreshold,
		updateCartMutation,
		isFetching,
		isLoading,
		isError,
		dict: cartDict,
		showRequiredApprovalMessage,
	};
}
